import React from 'react';
import { Platform } from 'react-native';
import { useToken, Box } from '@gluestack-ui/themed';
import { Image } from 'expo-image';
import { BlurView } from 'expo-blur';
import { LinearGradient } from 'expo-linear-gradient';
import Animated, {
  useAnimatedStyle,
  interpolate,
  Extrapolation,
  SharedValue,
} from 'react-native-reanimated';
import { Module } from '../types';
import { getColorForModule } from '../../../common/utils/getColorForModule';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const FALLBACK_IMAGE = require('@assets/img/fallback-module-image.jpg');

interface BackgroundImageProps {
  /** The module whose atmosphere is being rendered */
  module: Module;
  /** Index of the module in the list */
  index: number;
  /** Shared value of the vertical scroll position */
  scrollY: SharedValue<number>;
  /** Shared value of the page height */
  pageHeightSV: SharedValue<number>;
}

/**
 * BackgroundImage renders the immersive, animated background for a module.
 * It handles the "atmosphere" effect with tinting, blurring, and transitions.
 */
export const BackgroundImage: React.FC<BackgroundImageProps> = ({
  module, index, scrollY, pageHeightSV,
}) => {
  // @ts-expect-error - bgApp is a semantic token
  const bgApp = useToken('colors', 'bgApp') || '#FFFFFF';
  const colorName = getColorForModule(module);
  // @ts-expect-error - colorName50 is a dynamic token
  const moduleColor50 = useToken('colors', `${colorName}50`);

  const animatedImageStyle = useAnimatedStyle(() => {
    const height = Math.max(pageHeightSV.value, 100);
    const opacity = interpolate(
      scrollY.value,
      [(index - 0.6) * height, index * height, (index + 0.6) * height],
      [0, 1.0, 0],
      Extrapolation.CLAMP,
    );
    return { opacity };
  });

  const absoluteFill = {
    position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0,
  };

  return (
    <Animated.View style={[absoluteFill, animatedImageStyle]} pointerEvents="none">
      <Box style={absoluteFill} bg={moduleColor50 || bgApp} />
      <Image
        source={module.imageUrl || FALLBACK_IMAGE}
        // eslint-disable-next-line react-native/no-inline-styles
        style={[absoluteFill, { opacity: 0.25 }]}
        contentFit="cover"
        transition={300}
      />
      {Platform.OS !== 'web' && <BlurView intensity={20} style={absoluteFill} tint="light" />}
      <Box position="absolute" top={0} left={0} right={0} bottom={0} bg={bgApp} opacity={0.4} />
      <LinearGradient colors={['transparent', bgApp]} locations={[0.7, 1]} style={absoluteFill} />
    </Animated.View>
  );
};
